import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle } from "lucide-react";
import type { ContractDocumentDTO } from "@/modules/contracts/domain/contracts";
import { useContractQuery, useUpdateContract } from "../api/queries";
import {
  ContractEditTour,
  useContractEditTour,
} from "../tour/ContractEditTour";
import { ContractForm } from "../ui/ContractForm";

export function ContractEditPage() {
  const tour = useContractEditTour();
  const nav = useNavigate({ from: "/app/contracts/$id/edit" });
  const { id } = useParams({ from: "/app/contracts/$id/edit" });
  const contractQuery = useContractQuery(id);
  const updateContract = useUpdateContract(id);

  if (contractQuery.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
          </div>
        </Main>
      </>
    );
  }

  if (!contractQuery.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center p-8">
            <div className="text-destructive">ບໍ່ພົບສັນຍາ</div>
          </div>
        </Main>
      </>
    );
  }

  const contract = contractQuery.data;

  return (
    <>
      <Header />
      <Main>
        <ContractEditTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        {/* Page Header */}
        <div className="mb-6">
          <div className="mb-4 flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-3">
                <h1 className="font-bold text-3xl">ແກ້ໄຂສັນຍາ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ແກ້ໄຂຂໍ້ມູນສັນຍາ. ທຸກຊ່ອງທີ່ມີເຄື່ອງຫມາຍ * ແມ່ນຊ່ອງບັງຄັບ.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.history.back()}
              >
                <ArrowLeftIcon className="h-4 w-4" />
                ກັບ
              </Button>
            </div>
          </div>
        </div>

        {/* Form Container */}
        <div className="mx-auto max-w-5xl">
          <div className="rounded-xl border bg-card p-6">
            <ContractForm
              key={contract.id}
              initialDocuments={contract.documents
                .filter(
                  (d: ContractDocumentDTO) =>
                    d.documentType === "main" ||
                    d.documentType === "attachment",
                )
                .map((d: ContractDocumentDTO) => ({
                  id: d.id,
                  title: d.title,
                  fileKey: d.fileKey,
                  documentType: d.documentType,
                  notes: d.notes ?? null,
                }))}
              initialValues={{
                contractNumber: contract.contractNumber ?? undefined,
                title: contract.title,
                typeId: contract.typeId,
                status: contract.status,
                counterparty: contract.counterparty,
                counterpartyContact: contract.counterpartyContact ?? undefined,
                customerId: contract.customerId ?? undefined,
                quotationDocumentId: contract.quotationDocumentId ?? undefined,
                departmentId: contract.departmentId ?? undefined,
                employeeId: contract.employeeId ?? undefined,
                responsiblePersonId: contract.responsiblePersonId ?? undefined,
                startDate: contract.startDate,
                endDate: contract.endDate,
                autoRenewal: contract.autoRenewal,
                renewalNoticeDays: contract.renewalNoticeDays,
                notes: contract.notes ?? undefined,
                warnings: contract.warnings ?? undefined,
                specialConditions: contract.specialConditions ?? undefined,
              }}
              onSubmit={async (vals) => {
                const { documents, ...updateData } = vals;
                await updateContract.mutateAsync({
                  ...updateData,
                  customerId: updateData.customerId ?? null,
                  quotationDocumentId: updateData.quotationDocumentId ?? null,
                  departmentId: updateData.departmentId ?? null,
                  employeeId: updateData.employeeId ?? null,
                  responsiblePersonId: updateData.responsiblePersonId ?? null,
                  documents: documents ?? [],
                });
                nav({ to: "/app/contracts/$id", params: { id } });
              }}
              submitting={updateContract.isPending}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
